import { Alert, Button, Collapse, Form, Input, InputNumber, List, Modal, Space, Table, Tag, Tooltip, Typography, App as AntApp } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { formatDate } from "../app/classes.ts";
import {
  gradingKeys,
  HOMEWORK_MAX_LENGTH,
  studentFullName,
  TOPIC_MAX_LENGTH,
  type AssessmentKind,
  type AssessmentTypeDetails,
  type LessonGrades,
  type StudentMarks,
} from "../app/grading.ts";
import type { LessonDetails } from "../app/lessons.ts";
import { formatSchoolDateTime } from "../app/time.ts";

interface Props {
  lesson: LessonDetails | undefined;
  onClose(): void;
}

interface ContentForm {
  topic?: string;
  homework?: string;
}

/** A changed cell: the new value, or null to remove the mark. */
type Draft = Record<string, number | null>;

const cellKey = (studentId: string, kind: AssessmentKind) => `${studentId}:${kind}`;

/**
 * A lesson's grade sheet (GET /grading/lessons/{id}): the roster on the lesson date by assessment type, plus the topic and homework.
 * The teacher of record (or staff holding grading.manage) types marks into the grid: Tab moves across, Enter or the arrow keys move
 * down and up a column, an emptied cell removes the mark. Only changed cells are sent (PUT .../marks), the topic and homework with
 * PUT .../content. Marks are read-only while a closed marking period covers the lesson date. The server decides; the dialog only
 * hides what the user cannot do.
 */
export function LessonGradesModal({ lesson, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ContentForm>();
  const [draft, setDraft] = useState<Draft>({});
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string[]>([]);
  const grid = useRef<HTMLDivElement>(null);
  const lessonId = lesson?.id ?? "";

  const grades = useQuery({
    queryKey: gradingKeys.lesson(lessonId),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/lessons/{lessonId}", { params: { path: { lessonId } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: lesson !== undefined,
  });
  const edits = useQuery({
    queryKey: gradingKeys.edits(lessonId),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/lessons/{lessonId}/edits", { params: { path: { lessonId } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: lesson !== undefined,
  });

  const data = grades.data;
  useEffect(() => {
    setDraft({});
    setCellErrors({});
    setSummary([]);
  }, [lessonId]);
  useEffect(() => {
    if (data) form.setFieldsValue({ topic: data.topic ?? "", homework: data.homework ?? "" });
  }, [data, form]);

  const marksEditable = data !== undefined && data.mayEdit && !data.isClosed;
  const save = useMutation({
    mutationFn: async (content: ContentForm) => saveAll(lessonId, data, draft, content),
    onSuccess: async (saved) => {
      if (saved) queryClient.setQueryData(gradingKeys.lesson(lessonId), saved);
      setDraft({});
      setCellErrors({});
      setSummary([]);
      await queryClient.invalidateQueries({ queryKey: gradingKeys.all });
      void message.success(t("grading.saved"));
    },
    onError: (err) => {
      const errors = err instanceof ApiError ? err.fieldErrors() : {};
      setCellErrors(cellErrorsOf(errors, changedCells(draft)));
      setSummary(err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]);
    },
  });

  /** Enter and the arrow keys move down or up the same column; Tab keeps its normal order across the row. */
  const onCellKey = (event: KeyboardEvent<HTMLInputElement>, row: number, column: number) => {
    const step = event.key === "ArrowDown" || event.key === "Enter" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    const next = grid.current?.querySelector<HTMLInputElement>(`input[data-cell="${row + step}-${column}"]`);
    if (next) {
      event.preventDefault();
      next.focus();
      next.select();
    }
  };

  const markColumn = (type: AssessmentTypeDetails, column: number) => ({
    title: (
      <Space orientation="vertical" size={0}>
        <span>{type.name}</span>
        <Typography.Text type="secondary" style={{ fontWeight: "normal" }}>{t("grading.maxScore", { max: Number(type.maxScore) })}</Typography.Text>
      </Space>
    ),
    key: type.kind,
    width: 120,
    render: (_: unknown, s: StudentMarks, row: number) => {
      const key = cellKey(s.studentId, type.kind);
      const saved = s.marks.find((m) => m.kind === type.kind);
      const value = key in draft ? (draft[key] ?? null) : saved ? Number(saved.value) : null;
      const editable = marksEditable && s.onRoster;
      if (!editable) {
        return saved ? <MarkTag value={Number(saved.value)} edited={saved.isEdited} by={saved.enteredByName} at={saved.enteredAt} /> : <Typography.Text type="secondary">—</Typography.Text>;
      }
      return (
        <Tooltip title={cellErrors[key]} open={cellErrors[key] ? undefined : false}>
          <InputNumber
            data-cell={`${row}-${column}`}
            aria-label={t("grading.cellLabel", { name: studentFullName(s), type: type.name })}
            // No min/max here: InputNumber would silently clamp a typo like 7 to the maximum. The server rejects
            // out-of-range marks and the cell shows its error instead.
            precision={0}
            keyboard={false}
            controls={false}
            {...(cellErrors[key] ? { status: "error" as const } : {})}
            value={value}
            style={{ width: 72 }}
            onKeyDown={(e) => onCellKey(e, row, column)}
            onChange={(next) => setDraft((d) => ({ ...d, [key]: typeof next === "number" ? next : null }))}
          />
        </Tooltip>
      );
    },
  });

  const columns = [
    {
      title: t("grading.student"),
      key: "student",
      fixed: "left" as const,
      render: (_: unknown, s: StudentMarks) => (
        <Space orientation="vertical" size={0}>
          <span>{studentFullName(s)}</span>
          {!s.onRoster && <Typography.Text type="secondary">{t("grading.offRoster")}</Typography.Text>}
        </Space>
      ),
    },
    ...(data?.assessmentTypes ?? []).map(markColumn),
  ];

  const changed = changedCells(draft).length > 0;
  const studentName = (id: string) => {
    const s = data?.students.find((x) => x.studentId === id);
    return s ? studentFullName(s) : "";
  };
  const typeName = (kind: AssessmentKind) => data?.assessmentTypes.find((x) => x.kind === kind)?.name ?? kind;

  return (
    <Modal
      open={lesson !== undefined}
      title={t("grading.title")}
      onCancel={onClose}
      width={880}
      destroyOnHidden
      footer={
        <Space wrap>
          <Button onClick={onClose}>{t("common.close")}</Button>
          {data?.mayEdit && (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              {t("common.save")}
            </Button>
          )}
        </Space>
      }
    >
      {lesson && (
        <Typography.Paragraph type="secondary">
          {t("lessons.lessonLabel", { subject: lesson.subjectName, date: formatDate(lesson.date), period: Number(lesson.period) })}
        </Typography.Paragraph>
      )}
      {grades.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void grades.refetch()}>{t("app.retry")}</Button>} />}
      {grades.isPending && lesson !== undefined && <Table rowKey="studentId" size="small" columns={columns} dataSource={[]} loading pagination={false} />}
      {data && (
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size={8}>
            {data.isClosed && <Tag icon={<LockOutlined />} color="orange">{t("grading.closed")}</Tag>}
            {data.lessonStatus === "Cancelled" && <Tag color="red">{t("lessons.statuses.Cancelled")}</Tag>}
            {marksEditable && <Typography.Text type="secondary">{t("grading.keyboardHint")}</Typography.Text>}
            {changed && <Tag color="blue">{t("grading.unsaved")}</Tag>}
          </Space>
          {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} />}
          <div ref={grid}>
            <Table<StudentMarks>
              rowKey="studentId"
              size="small"
              columns={columns}
              dataSource={data.students}
              pagination={false}
              locale={{ emptyText: t("grading.emptyRoster") }}
              scroll={{ x: "max-content" }}
            />
          </div>
          <Form form={form} layout="vertical" disabled={!data.mayEdit} onFinish={(values) => save.mutate(values)}>
            <Form.Item name="topic" label={t("grading.topic")} rules={[{ max: TOPIC_MAX_LENGTH }]}>
              <Input maxLength={TOPIC_MAX_LENGTH} placeholder={t("grading.topicPlaceholder")} />
            </Form.Item>
            <Form.Item
              name="homework"
              label={t("grading.homework")}
              rules={[{ max: HOMEWORK_MAX_LENGTH }]}
              extra={data.contentEditedByName && data.contentEditedAt ? t("grading.contentEdited", { name: data.contentEditedByName, time: formatSchoolDateTime(data.contentEditedAt) }) : undefined}
            >
              <Input.TextArea rows={3} maxLength={HOMEWORK_MAX_LENGTH} placeholder={t("grading.homeworkPlaceholder")} />
            </Form.Item>
          </Form>
          <Collapse
            size="small"
            items={[
              {
                key: "edits",
                label: t("grading.editLog", { count: edits.data?.length ?? 0 }),
                children: (
                  <List
                    size="small"
                    loading={edits.isPending}
                    dataSource={edits.data ?? []}
                    locale={{ emptyText: t("grading.noEdits") }}
                    renderItem={(e) => (
                      <List.Item>
                        <Space orientation="vertical" size={0}>
                          <span>
                            {t(e.newValue === null ? "grading.editRemoved" : "grading.editChanged", {
                              name: studentName(e.studentId),
                              type: typeName(e.kind),
                              from: Number(e.oldValue),
                              to: e.newValue === null ? "" : Number(e.newValue),
                            })}
                          </span>
                          <Typography.Text type="secondary">{`${e.actorName}, ${formatSchoolDateTime(e.occurredAt)}`}</Typography.Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                ),
              },
            ]}
          />
        </Space>
      )}
    </Modal>
  );
}

function MarkTag({ value, edited, by, at }: { value: number; edited: boolean; by: string; at: string }) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("grading.enteredBy", { name: by, time: formatSchoolDateTime(at) })}>
      <Tag color={edited ? "gold" : "default"} style={{ minWidth: 32, textAlign: "center" }}>
        {value}
        {edited ? "*" : ""}
      </Tag>
    </Tooltip>
  );
}

function changedCells(draft: Draft): [string, number | null][] {
  return Object.entries(draft);
}

/** Maps `marks[i].value` / `marks[i].studentId` errors back to the cell that was sent at index i. */
function cellErrorsOf(errors: Record<string, string[]>, sent: [string, number | null][]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [path, messages] of Object.entries(errors)) {
    const index = /^marks\[(\d+)\]/.exec(path)?.[1];
    const cell = index === undefined ? undefined : sent[Number(index)];
    if (cell) result[cell[0]] = messages.join(" ");
  }
  return result;
}

/** Sends the changed cells, then the topic and homework when they differ; returns the latest grade sheet. */
async function saveAll(lessonId: string, data: LessonGrades | undefined, draft: Draft, content: ContentForm): Promise<LessonGrades | undefined> {
  let latest = data;
  const cells = changedCells(draft);
  if (cells.length > 0) {
    const marks = cells.map(([key, value]) => {
      const [studentId = "", kind] = key.split(":");
      return { studentId, kind: kind as AssessmentKind, value };
    });
    const { data: saved, error, response } = await api.PUT("/grading/lessons/{lessonId}/marks", { params: { path: { lessonId } }, body: { marks } });
    if (!saved) throw new ApiError(response.status, error);
    latest = saved;
  }

  const topic = content.topic?.trim() || null;
  const homework = content.homework?.trim() || null;
  if (topic !== (data?.topic ?? null) || homework !== (data?.homework ?? null)) {
    const { data: saved, error, response } = await api.PUT("/grading/lessons/{lessonId}/content", { params: { path: { lessonId } }, body: { topic, homework } });
    if (!saved) throw new ApiError(response.status, error);
    latest = saved;
  }
  return latest;
}
